import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { X, Copy, Check } from 'lucide-react';
import type { UserActionLogItem } from 'services/auditApi';
import { METHOD_STYLES, formatTimestamp } from './helpers';

interface PayloadDrawerProps {
  item: UserActionLogItem;
  onClose: () => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--color-text-muted)',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: 13, color: 'var(--color-text-primary)', wordBreak: 'break-word' }}>
        {value}
      </Box>
    </Box>
  );
}

export default function PayloadDrawer({ item, onClose }: PayloadDrawerProps) {
  const [copied, setCopied] = useState(false);
  const method = METHOD_STYLES[item.http_method];
  const payloadJson =
    item.payload && Object.keys(item.payload).length > 0
      ? JSON.stringify(item.payload, null, 2)
      : null;

  const handleCopy = () => {
    if (!payloadJson) return;
    navigator.clipboard.writeText(payloadJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <>
      <Box
        onClick={onClose}
        sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(13,17,23,0.32)', zIndex: 299 }}
      />
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          height: '100vh',
          width: 480,
          maxWidth: '100vw',
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          borderLeft: '1px solid rgba(255,255,255,0.72)',
          boxShadow: '0 0 40px rgba(20,116,255,0.14), -1px 0 0 rgba(20,116,255,0.10)',
          zIndex: 300,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)' }}>
            Детали записи
          </Typography>
          <Box
            onClick={onClose}
            sx={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              '&:hover': { bgcolor: 'var(--color-bg)', color: 'var(--color-text-primary)' },
            }}
          >
            <X size={16} />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Row label="Время" value={formatTimestamp(item.created_at)} />
            <Row label="Пользователь" value={item.user_email ?? item.user_id} />
            <Row label="Проект" value={item.project_name ?? '—'} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Row label="Категория" value={item.category} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Row label="Действие" value={item.action} />
              </Box>
            </Box>
            <Row
              label="Метод"
              value={
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    bgcolor: method?.bg ?? 'var(--color-surface-subtle)',
                    color: method?.color ?? 'var(--color-text-secondary)',
                    borderRadius: '6px',
                    px: 1,
                    py: '2px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {item.http_method}
                </Box>
              }
            />
            <Row
              label="URL"
              value={
                <Box
                  component="code"
                  sx={{
                    fontSize: 12,
                    fontFamily: 'monospace',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {item.url}
                </Box>
              }
            />

            {/* Payload */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Payload
                </Typography>
                {payloadJson && (
                  <Box
                    onClick={handleCopy}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: 11,
                      fontWeight: 600,
                      color: copied ? 'var(--color-success)' : 'var(--color-text-muted)',
                      cursor: 'pointer',
                      '&:hover': { color: 'var(--color-primary)' },
                    }}
                  >
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? 'Скопировано' : 'Копировать'}
                  </Box>
                )}
              </Box>
              {payloadJson ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 'var(--radius-md)',
                    bgcolor: 'var(--color-surface-subtle)',
                    border: '1px solid var(--color-border)',
                    fontSize: 12,
                    fontFamily: 'monospace',
                    color: 'var(--color-text-primary)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowX: 'auto',
                  }}
                >
                  {payloadJson}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                  Без дополнительных данных
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
